import {FC, useState, useRef, useEffect, useContext} from 'react';
import {Typography, Box} from '@mui/material';
import {ContactsUploaderStyle} from './ContactsUploader.styles';
import Button from '@/shared/components/Button';
import * as XLSX from 'xlsx';
import Radio from '@/shared/components/Radio';
import {UploadContactListContext} from '../../../UploadContactListContext';

export const ContactsUploader: FC = () => {
	const [step, setStep] = useState(0);
	const [fileName, setFileName] = useState<string[]>([]);
	const inputRef = useRef<HTMLInputElement | null>(null);

	const {handleNextStep, selectedFile, setSelectedFile, sheetName, setSheetName} = useContext(UploadContactListContext);

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file && isValidFile(file)) {
			setSelectedFile(file);
			setStep(1);
		}
	};

	useEffect(() => {
		const getSheetNamesFromFile = async (file: File): Promise<string[]> => {
			const data = await file.arrayBuffer();
			const workbook = XLSX.read(data, {type: 'array'});

			setFileName(workbook.SheetNames);
			return workbook.SheetNames;
		};
		if (!selectedFile) return;
		getSheetNamesFromFile(selectedFile as File);
	}, [selectedFile]);

	const isValidFile = (file: File) => {
		const allowedTypes = [
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			'application/vnd.ms-excel',
			'text/csv',
		];
		return allowedTypes.includes(file.type);
	};

	const handleRadioButtonChange = (value: string) => {
		setSheetName(value);
	};

	return (
		<Box sx={ContactsUploaderStyle.wrapper}>
			{step === 0 ? (
				<>
					<Typography sx={ContactsUploaderStyle.title}>Data that we expect:</Typography>
					<Box sx={ContactsUploaderStyle.list}>
						<Typography variant="caption" sx={ContactsUploaderStyle.item}>
							First Name
						</Typography>
						<Typography variant="caption" sx={ContactsUploaderStyle.item}>
							Last Name
						</Typography>
						<Typography variant="caption" sx={ContactsUploaderStyle.item}>
							Email
						</Typography>
						<Typography variant="caption" sx={ContactsUploaderStyle.item}>
							Phone
						</Typography>
						<Typography variant="caption" sx={ContactsUploaderStyle.item}>
							Meal
						</Typography>
						<Typography variant="caption" sx={ContactsUploaderStyle.item}>
							Address
						</Typography>
						<Typography variant="caption" sx={ContactsUploaderStyle.item}>
							City
						</Typography>
						<Typography variant="caption" sx={ContactsUploaderStyle.item}>
							State
						</Typography>
						<Typography variant="caption" sx={ContactsUploaderStyle.item}>
							Zip
						</Typography>
					</Box>
					<Box sx={ContactsUploaderStyle.uploaderArea}>
						<Typography sx={ContactsUploaderStyle.text}>Upload .xlsx, .xls or .csv file</Typography>

						<input
							type="file"
							accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
							style={{display: 'none'}}
							id="contacts-file"
							onChange={handleFileChange}
							ref={inputRef}
						/>
						<Button title="Select file" onClick={() => inputRef.current?.click()} />
					</Box>
				</>
			) : (
				<Box sx={ContactsUploaderStyle.radioGroup}>
					<Typography sx={ContactsUploaderStyle.title}>Select the sheet to use:</Typography>
					{fileName.map((name, index) => (
						<Radio
							key={index}
							label={`Sheet number ${index + 1}`}
							value={name}
							checked={sheetName === name}
							onChange={(event) => handleRadioButtonChange(event.target.value)}
						/>
					))}
					<Box sx={ContactsUploaderStyle.buttonWrapper}>
						<Button title="Next Step" disabled={sheetName === ''} onClick={handleNextStep} />
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default ContactsUploader;
